import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fetchUsers } from '../../services/api';
import { AdminUser } from '../../types';
import Navbar from '../shared/Navbar';

const ApiKeySection: React.FC = () => {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getApiKey();
        setApiKey(res.api_key);
      } catch {
        // ignore – user may not have a key yet
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleGenerate = async () => {
    const msg = apiKey
      ? 'This will replace your existing API key. Any scripts using the old key will stop working. Continue?'
      : 'Generate a new API key?';
    if (!window.confirm(msg)) return;
    try {
      setGenerating(true);
      const res = await api.generateApiKey();
      setApiKey(res.api_key);
      setRevealed(true);
      setCopied(false);
    } catch (e: any) {
      alert(e?.message || 'Failed to generate API key');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const maskedKey = apiKey ? apiKey.slice(0, 8) + '••••••••••••••••' : null;

  return (
    <div className="api-key-section">
      <div className="api-key-header">
        <h3>🔑 API Key</h3>
        <span className="ml-subtle">Use this key to authenticate CLI and script requests via the <code>X-API-Key</code> header.</span>
      </div>
      <div className="api-key-body">
        {loading ? (
          <span className="ml-subtle">Loading…</span>
        ) : apiKey ? (
          <div className="api-key-display">
            <code className="api-key-value">{revealed ? apiKey : maskedKey}</code>
            <button className="tm-button tm-button--primary ml-replace-btn" onClick={() => setRevealed(r => !r)} title={revealed ? 'Hide' : 'Reveal'}>
              {revealed ? '🙈 Hide' : '👁 Reveal'}
            </button>
            <button className="tm-button tm-button--primary ml-replace-btn" onClick={handleCopy} title="Copy to clipboard">
              {copied ? '✅ Copied' : '📋 Copy'}
            </button>
            <button className="tm-button tm-button--primary ml-replace-btn" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating…' : '🔄 Regenerate'}
            </button>
          </div>
        ) : (
          <div className="api-key-display">
            <span className="ml-subtle">No API key generated yet.</span>
            <button className="tm-button tm-button--primary ml-replace-btn" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating…' : '✨ Generate API Key'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const ManageUsersPage: React.FC = () => {
  const [me, setMe] = useState<{ authenticated: boolean; user?: { email: string; is_admin: boolean } } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const current = await api.me();
        setMe(current);
        if (!(current?.authenticated && current?.user?.is_admin)) {
          setLoading(false);
          return;
        }
        setLoading(true);
        setUsers(await fetchUsers());
      } catch (e) {
        setError('Failed to load users');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    void bootstrap();
  }, []);

  const hasUsers = useMemo(() => users.length > 0, [users]);

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container">
          <div className="page-intro">
            <h2>Manage Users</h2>
            <p>Select a user to review activity and account settings.</p>
          </div>
          {!(me?.authenticated && me?.user?.is_admin) && (
            <div className="admin-access-warning">
              <div className="tm-label">Admin access required</div>
              <div className="tm-mb-sm">You must be signed in as an admin to manage users.</div>
              <div className="tm-actions">
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/login">Sign In</Link>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/">Back to Home</Link>
              </div>
            </div>
          )}
          {!(me?.authenticated && me?.user?.is_admin) ? null : (
            <>
            <ApiKeySection />
            <div className="manage-users-list">
              <div className="mu-header">
                <h3>Users</h3>
                {loading && <span className="ml-subtle">Loading…</span>}
                {error && <span className="ml-error">{error}</span>}
              </div>
              <div className="mu-items">
                {!hasUsers && !loading ? (
                  <div className="ml-empty">No users found.</div>
                ) : (
                  users.map((u) => (
                    <div key={u.id} className="mu-item">
                      <div className="mu-item-row">
                        <div className="mu-main">
                          <div className="mu-email">
                            <Link to={`/manage/user/${u.id}`}>{u.email}</Link>
                          </div>
                          <div className="mu-meta">
                            <span className={`mu-badge ${u.is_admin ? 'admin' : 'user'}`}>{u.is_admin ? 'Admin' : 'User'}</span>
                            <span className="mu-divider">·</span>
                            <span className="mu-count">Chats: {u.chat_count ?? 0}</span>
                            {u.organisation_name && (
                              <>
                                <span className="mu-divider">·</span>
                                <Link
                                  className="mu-organisation-summary"
                                  to={`/manage/${u.organisation_id}`}
                                >
                                  {u.organisation_name} ({u.organisation_role?.toLowerCase()})
                                </Link>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManageUsersPage;

